/**
 * ClientsView - Full-screen view for clients management
 * Matches the visual style of the Library view
 */

import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { TabGroup, TabItem } from "@/components/ui/TabGroup";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ClientsTable } from "./components/ClientsTable";
import { CreateClientModal } from "./components/CreateClientModal";
import { useClients } from "./useClients";
import { useAllFollowerStats } from "@/hooks/useFollowerStats";
import { useAllReportSummaries } from "@/hooks/useReportSummaries";
import { useGoogleClientSummaries } from "@/domains/Reporting/hooks/useGoogleClientSummaries";
import { useAllDataHealth, useSyncAudienceActivity } from "@/hooks/useDataHealth";
import { clientService } from "./Client.service";
import type { Client, ClientStatus } from "@/types";
import { CLIENT_STATUS_CONFIG } from "./clientStatusConfig";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";
import { EntityCreateButton } from "@/core/entityConfig";

type StatusFilter = "ALL" | "ACTIVE" | "PROPOSAL" | "ARCHIVED";
type SortField = "name" | "relationship_started" | "followers" | "engagement" | "last_activity" | "growth";

export function ClientsView() {
  const navigate = useNavigate();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ACTIVE");
  // Sort defaults to name A-Z — the sort dropdown was dropped; the Growth
  // header toggles between growth (highest first) and name.
  const [sortBy, setSortBy] = useState<SortField>("name");
  const [sortOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");
  // AdminLayout's header slot for the page's primary action (New Client).
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderSlot(document.getElementById("admin-header-actions"));
  }, []);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clientToArchive, setClientToArchive] = useState<string | null>(null);
  const [clientToUnarchive, setClientToUnarchive] = useState<string | null>(null);

  // Data fetching
  const { clients, isLoading, addClient, refetch } = useClients();
  const { data: followerStats } = useAllFollowerStats();

  // Report summaries for previous month (completed data)
  const now = new Date();
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const { data: reportSummaries } = useAllReportSummaries(prevYear, prevMonth);
  const {
    data: googleSummaries,
    isLoading: googleSummariesLoading,
    isError: googleSummariesError,
  } = useGoogleClientSummaries(prevYear, prevMonth);

  // Data health for all clients
  const { data: dataHealth, refetch: refetchHealth } = useAllDataHealth();
  const { sync: syncData, isSyncing } = useSyncAudienceActivity();

  // Archive/Unarchive mutations
  const archiveMutation = useMutation({
    mutationFn: (clientId: string) => clientService.archiveClient(clientId),
    onSuccess: () => {
      refetch();
      setClientToArchive(null);
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: (clientId: string) => clientService.unarchiveClient(clientId),
    onSuccess: () => {
      refetch();
      setClientToUnarchive(null);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ clientId, status }: { clientId: string; status: ClientStatus }) =>
      clientService.updateClientStatus(clientId, status),
    onSuccess: () => {
      refetch();
    },
  });

  // Filter and sort clients
  const filteredClients = useMemo(() => {
    let result = [...clients];

    // Status filter
    if (statusFilter === "ACTIVE") {
      result = result.filter((c) => c.status === "ACTIVE");
    } else if (statusFilter === "PROPOSAL") {
      result = result.filter((c) => c.status === "PROPOSAL");
    } else if (statusFilter === "ARCHIVED") {
      result = result.filter((c) => c.status === "ARCHIVED");
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((c) =>
        c.clientName.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        case "relationship_started":
          const dateA = a.relationshipStartedAt ? new Date(a.relationshipStartedAt).getTime() : 0;
          const dateB = b.relationshipStartedAt ? new Date(b.relationshipStartedAt).getTime() : 0;
          comparison = dateB - dateA;
          break;
        case "followers":
          const statsA = followerStats?.[a.id]?.stats?.instagram?.current || a.instagramStartFollowers || 0;
          const statsB = followerStats?.[b.id]?.stats?.instagram?.current || b.instagramStartFollowers || 0;
          comparison = statsB - statsA;
          break;
        case "engagement":
          const engA = reportSummaries?.[a.id]?.totalEngagement || 0;
          const engB = reportSummaries?.[b.id]?.totalEngagement || 0;
          comparison = engB - engA;
          break;
        case "last_activity":
          const activityA = a.stats?.lastActivity ? new Date(a.stats.lastActivity).getTime() : 0;
          const activityB = b.stats?.lastActivity ? new Date(b.stats.lastActivity).getTime() : 0;
          comparison = activityB - activityA;
          break;
        case "growth":
          // Highest rate first; clients without a rate sink to the bottom.
          const rateA = googleSummaries?.clients[a.id]?.growth.rate ?? null;
          const rateB = googleSummaries?.clients[b.id]?.growth.rate ?? null;
          comparison =
            rateA == null ? (rateB == null ? 0 : 1) : rateB == null ? -1 : rateB - rateA;
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [clients, statusFilter, searchQuery, sortBy, sortOrder, followerStats, reportSummaries, googleSummaries]);

  // Tab items
  const statusTabs: TabItem<StatusFilter>[] = [
    {
      id: "ALL",
      label: "Tutti",
      badge: clients.length,
    },
    {
      id: "ACTIVE",
      label: CLIENT_STATUS_CONFIG.ACTIVE.label,
      badge: clients.filter((c) => c.status === "ACTIVE").length,
    },
    {
      id: "PROPOSAL",
      label: CLIENT_STATUS_CONFIG.PROPOSAL.label,
      badge: clients.filter((c) => c.status === "PROPOSAL").length,
    },
    {
      id: "ARCHIVED",
      label: CLIENT_STATUS_CONFIG.ARCHIVED.label,
      badge: clients.filter((c) => c.status === "ARCHIVED").length,
    },
  ];

  const handleViewClient = (clientId: string) => {
    navigate(`/app/${clientId}`);
  };

  const handleArchive = (clientId: string) => {
    setClientToArchive(clientId);
  };

  const handleUnarchive = (clientId: string) => {
    setClientToUnarchive(clientId);
  };

  const handleEditClient = (client: Client) => {
    navigate(`/app/${client.id}/settings`);
  };

  const handleConfirmArchive = async () => {
    if (!clientToArchive) return;
    await archiveMutation.mutateAsync(clientToArchive);
  };

  const handleConfirmUnarchive = async () => {
    if (!clientToUnarchive) return;
    await unarchiveMutation.mutateAsync(clientToUnarchive);
  };

  const handleSyncData = async (clientId: string) => {
    const success = await syncData(clientId);
    if (success) {
      refetchHealth();
    }
  };

  const handleStatusChange = (clientId: string, status: ClientStatus) => {
    updateStatusMutation.mutate({ clientId, status });
  };

  const clientToArchiveName = clientToArchive
    ? clients.find((c) => c.id === clientToArchive)?.clientName
    : "";

  const clientToUnarchiveName = clientToUnarchive
    ? clients.find((c) => c.id === clientToUnarchive)?.clientName
    : "";

  return (
    <PageLayout>
      {/* New Client lives in the AdminLayout header (portal slot). */}
      {headerSlot &&
        createPortal(
          <EntityCreateButton
            entity="client"
            onClick={() => setShowCreateModal(true)}
          />,
          headerSlot
        )}

      <PageHeader>
        <PageHeader.Row>
          <TabGroup
            items={statusTabs}
            activeId={statusFilter}
            onTabChange={setStatusFilter}
            config={{
              variant: "navigation",
              size: "sm",
              layout: "horizontal",
              hasBackground: true,
            }}
          />
          <PageHeader.Actions>
            <div className="relative w-64">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
              />
            </div>
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>

      <PageSection.Card>
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <ClientsTable
            clients={filteredClients}
            followerStats={followerStats}
            reportSummaries={reportSummaries}
            googleSummaries={googleSummaries?.clients}
            agencyGrowth={googleSummaries?.agency}
            googleSummariesState={
              googleSummariesLoading ? "loading" : googleSummariesError ? "error" : "ready"
            }
            sortedByGrowth={sortBy === "growth"}
            onToggleGrowthSort={() => setSortBy((s) => (s === "growth" ? "name" : "growth"))}
            dataHealth={dataHealth}
            reportMonth={prevMonth}
            reportYear={prevYear}
            onViewClient={handleViewClient}
            onEditClient={handleEditClient}
            onArchiveClient={handleArchive}
            onUnarchiveClient={handleUnarchive}
            onStatusChange={handleStatusChange}
            onSyncData={handleSyncData}
            syncingClientId={isSyncing}
            onCreateClient={() => setShowCreateModal(true)}
          />
        )}
      </PageSection.Card>

      {/* Archive confirmation */}
      <ConfirmDialog
        isOpen={!!clientToArchive}
        onClose={() => setClientToArchive(null)}
        onConfirm={handleConfirmArchive}
        title="Archive Client"
        message={`Are you sure you want to archive "${clientToArchiveName}"? The client will be moved to the archived section.`}
        confirmText="Archive"
        variant="warning"
        isLoading={archiveMutation.isPending}
      />

      {/* Unarchive confirmation */}
      <ConfirmDialog
        isOpen={!!clientToUnarchive}
        onClose={() => setClientToUnarchive(null)}
        onConfirm={handleConfirmUnarchive}
        title="Restore Client"
        message={`Are you sure you want to restore "${clientToUnarchiveName}"? The client will be moved back to active clients.`}
        confirmText="Restore"
        variant="info"
        isLoading={unarchiveMutation.isPending}
      />

      {/* Create client modal */}
      <CreateClientModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onClientCreated={(client) => {
          addClient(client);
          setShowCreateModal(false);
        }}
      />
    </PageLayout>
  );
}
